import React from "react";
import { contrastRatio, contrastLevel } from "./contrast";

interface Props {
  foreground: string | undefined | null;
  background: string | undefined | null;
  enabled?: boolean;
  /** Optional one-line remediation hint, appended to the tooltip. */
  remediation?: string;
}

/**
 * Renders a small WCAG AA contrast warning icon when the ratio is below 4.5:1.
 * Hidden entirely when enabled=false or ratio is acceptable.
 */
export default function ContrastWarning({ foreground, background, enabled = true, remediation }: Props) {
  if (!enabled) return null;
  const ratio = contrastRatio(foreground, background);
  if (ratio === null) return null;
  const level = contrastLevel(ratio);
  if (level === "pass") return null;

  const color = level === "fail" ? "text-red-600" : "text-amber-500";
  const formatted = ratio.toFixed(2);
  const baseTip =
    level === "fail"
      ? `Contrast ${formatted}:1 — far below WCAG AA (4.5:1). Text will be hard to read.`
      : `Contrast ${formatted}:1 — below WCAG AA (4.5:1). Text may be hard to read.`;
  const tip = remediation ? `${baseTip} ${remediation}` : baseTip;

  return (
    <span
      className={`inline-flex items-center ${color}`}
      title={tip}
      aria-label={tip}
      role="img"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
      >
        <path
          fillRule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
      <span className="text-xs ml-1 font-medium">{formatted}:1</span>
    </span>
  );
}
